import React from "react";
import { Star, ExternalLink, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "./ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "./ui/carousel";
import { testimonials, googleBusinessInfo } from "../data/mock";

// Google "G" logo inline SVG - official brand colors
const GoogleG = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

const ReviewCard = ({ t }) => {
  const initials = t.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="h-full bg-white rounded-3xl p-7 border border-emerald-100 shadow-sm hover:shadow-lg transition-shadow flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {t.photo ? (
            <img src={t.photo} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand to-emerald-700 text-white flex items-center justify-center font-bold text-sm">
              {initials}
            </div>
          )}
          <div>
            <div className="font-bold text-brand-dark text-sm leading-tight">{t.name}</div>
            <div className="text-[11px] text-brand-dark/60">{t.date} · Recensione locale</div>
          </div>
        </div>
        <GoogleG className="w-5 h-5 flex-shrink-0" />
      </div>

      <div className="flex items-center gap-1 mb-3">
        {[...Array(t.rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        ))}
        {t.verified && (
          <Badge className="ml-2 bg-emerald-50 text-brand hover:bg-emerald-50 border-0 text-[10px] gap-1 h-5 px-2">
            <ShieldCheck className="w-3 h-3" /> Verificata
          </Badge>
        )}
      </div>

      <p className="text-sm text-brand-dark/80 leading-relaxed flex-1">&ldquo;{t.text}&rdquo;</p>

      <div className="mt-4 pt-4 border-t border-emerald-50 text-[11px] text-brand-dark/50 font-medium">
        {t.role}
      </div>
    </div>
  );
};

const GoogleReviews = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header with Google branding */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-brand-light text-brand rounded-full px-4 py-1.5 mb-4 text-sm font-semibold">
              <GoogleG className="w-4 h-4" /> Recensioni Google
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark mb-4 leading-tight">
              Le parole dei <span className="italic text-brand">nostri studenti</span>
            </h2>
            <p className="text-lg text-brand-dark/70">
              Recensioni verificate direttamente dalla nostra scheda Google Business.
            </p>
          </div>

          {/* Rating summary */}
          <div className="bg-[#fafaf7] rounded-3xl p-6 border border-emerald-100 flex items-center gap-5 min-w-[280px]">
            <div className="text-center">
              <div className="font-display text-5xl font-bold text-brand leading-none">{googleBusinessInfo.rating.toFixed(1)}</div>
              <div className="flex justify-center gap-0.5 mt-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
            <div className="w-px h-16 bg-emerald-200"></div>
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <GoogleG className="w-4 h-4" />
                <span className="font-bold text-brand-dark text-sm">Google Business</span>
              </div>
              <div className="text-xs text-brand-dark/70">Basato su <strong>{googleBusinessInfo.reviewCount}+</strong> recensioni verificate</div>
              <a
                href={googleBusinessInfo.profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-dark mt-2"
              >
                Vedi su Google Maps <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>

        {/* Carousel */}
        <Carousel
          opts={{ align: "start", loop: true }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {testimonials.map((t, i) => (
              <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <ReviewCard t={t} />
              </CarouselItem>
            ))}
          </CarouselContent>

          <div className="flex items-center justify-between mt-8">
            <div className="flex items-center gap-2 text-sm text-brand-dark/60">
              <GoogleG className="w-4 h-4" />
              <span>Le nostre recensioni Google reali</span>
            </div>
            <div className="flex gap-2">
              <CarouselPrevious className="static translate-y-0 h-10 w-10 border-emerald-200 text-brand hover:bg-brand-light hover:text-brand" />
              <CarouselNext className="static translate-y-0 h-10 w-10 border-emerald-200 text-brand hover:bg-brand-light hover:text-brand" />
            </div>
          </div>
        </Carousel>

        {/* CTA */}
        <div className="mt-12 text-center">
          <a
            href={googleBusinessInfo.writeReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white rounded-full h-12 px-8 font-semibold transition-colors shadow-sm hover:shadow-md"
          >
            <GoogleG className="w-4 h-4" />
            <span>Scrivi una recensione su Google</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
